import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { AnimationOptions } from 'ngx-lottie'
import { ContentTypeKey, LibraryStateService } from 'src/app/service/state/library-state.service'
import {
  DataSearchResult,
  DaydicTypesIconNames,
  DefaultPagination,
  LibraryChips,
  SearchChips,
  Themes,
} from 'src/app/shared/enums/enums'

import { DdcEditDialogComponent } from 'src/app/shared/ddc/ddc-edit-dialog/ddc-edit-dialog.component'
import { DdcEditPairDialogComponent } from 'src/app/shared/ddc/ddc-edit-pair-dialog/ddc-edit-pair-dialog.component'
import { DdcEditWordDialogComponent } from 'src/app/shared/ddc/ddc-edit-word-dialog/ddc-edit-word-dialog.component'
import { DdcTabsChipsComponent } from '../../shared/ddc-tabs-chips/ddc-tabs-chips.component'

import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import { Observable, Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { AppState } from 'src/app/ngrx/states'
import { getTheme } from 'src/app/ngrx/store/selectors'
import { CategoryService } from 'src/app/service/data/category.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { FigureService } from 'src/app/service/data/figure.service'
import { NoteService } from 'src/app/service/data/note.service'
import { OverviewService } from 'src/app/service/data/overview.service'
import { PairService } from 'src/app/service/data/pair.service'
import { QuizService } from 'src/app/service/data/quiz.service'
import { TagService } from 'src/app/service/data/tag.service'
import { WordService } from 'src/app/service/data/word.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { PdfExportService, PdfsResponse } from 'src/app/service/pdf/pdf-export.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { DdcCatButtonComponent } from 'src/app/shared/ddc/ddc-cat-button/ddc-cat-button.component'
import { DdcContentCardComponent } from 'src/app/shared/ddc/ddc-content-card/ddc-content-card.component'
import { DdcDeleteDialogComponent } from 'src/app/shared/ddc/ddc-delete-dialog/ddc-delete-dialog.component'
import { DdcDesktopDialogComponent } from 'src/app/shared/ddc/ddc-desktop-dialog/ddc-desktop-dialog.component'
import { DdcLoadingSkeletonComponent } from 'src/app/shared/ddc/ddc-loading-skeleton/ddc-loading-skeleton.component'
import { DdcMobileSheetComponent } from 'src/app/shared/ddc/ddc-mobile-sheet/ddc-mobile-sheet.component'
import { DdcPdfButtonComponent } from 'src/app/shared/ddc/ddc-pdf-button/ddc-pdf-button.component'
import { DdcSmallPairComponent } from 'src/app/shared/ddc/ddc-small-pair/ddc-small-pair.component'
import { DdcSmallWordComponent } from 'src/app/shared/ddc/ddc-small-word/ddc-small-word.component'
import { ContentTypes } from 'src/app/shared/enums/enums'
import { Figure } from 'src/app/shared/models/main/figure'
import { Note } from 'src/app/shared/models/main/note'
import { Pair } from 'src/app/shared/models/main/pair'
import { DaydicPDF } from 'src/app/shared/models/main/pdf'
import { Quiz } from 'src/app/shared/models/main/quiz'
import { Word } from 'src/app/shared/models/main/word'
import { DdcStatLineChartComponent } from '../../shared/ddc-stat-line-chart/ddc-stat-line-chart.component'
import { DdcNotAvailableComponent } from '../../shared/ddc/ddc-not-available/ddc-not-available.component'
import { DdcTagButtonComponent } from '../../shared/ddc/ddc-tag-button/ddc-tag-button.component'
import { DeleteWordPairDicCommand } from '../commands/delete-word-pair-dic/delete-word-pair-dic.command'

export interface TabUIState {
  isSelected: boolean
  isLoading: boolean
  scrollLoading: boolean
  end: boolean
  isEmpty: boolean
  fetchError: string
  isLoaded: boolean
  isPgnLoading: boolean
  currentPage: number
  pageInfoLabel: string
  selectedRowIndex: number
}

const TAB_KEYS = [
  'overview',
  'words',
  'pairs',
  'dictionaries',
  'notes',
  'quizzes',
  'figures',
  'categories',
  'tags',
  'pdfs',
] as const
type TabKey = (typeof TAB_KEYS)[number]

const LIBRARY_TAB_KEYS: ContentTypeKey[] = [
  'words',
  'pairs',
  'dictionaries',
  'notes',
  'quizzes',
  'figures',
]

function createDefaultTabUI(): Record<TabKey, TabUIState> {
  const map = {} as Record<TabKey, TabUIState>
  for (const key of TAB_KEYS) {
    map[key] = {
      isSelected: false,
      isLoading: false,
      scrollLoading: false,
      end: false,
      isEmpty: false,
      fetchError: 'Empty.',
      isLoaded: false,
      isPgnLoading: false,
      currentPage: 1,
      pageInfoLabel: 'Scroll down to load more',
      selectedRowIndex: -1,
    }
  }
  return map
}

const CHIP_ID_TO_TAB: Record<number, TabKey> = {
  [LibraryChips.OVERVIEW]: 'overview',
  [LibraryChips.WORDS]: 'words',
  [LibraryChips.PAIRS]: 'pairs',
  [LibraryChips.DICTIONARIES]: 'dictionaries',
  [LibraryChips.CATEGORIES]: 'categories',
  [LibraryChips.TAGS]: 'tags',
  [LibraryChips.PDFS]: 'pdfs',
  [LibraryChips.NOTES]: 'notes',
  [LibraryChips.QUIZZES]: 'quizzes',
  [LibraryChips.FIGURES]: 'figures',
}

const DASHBOARD_ELEMENT_TO_ROUTE: Record<string, [string, number]> = {
  words: ['/library', LibraryChips.WORDS],
  pairs: ['/library', LibraryChips.PAIRS],
  dics: ['/library', LibraryChips.DICTIONARIES],
  notes: ['/library', LibraryChips.NOTES],
  quizzes: ['/library', LibraryChips.QUIZZES],
  figures: ['/library', LibraryChips.FIGURES],
  cats: ['/library', LibraryChips.CATEGORIES],
  tags: ['/library', LibraryChips.TAGS],
  pdfs: ['/library', LibraryChips.PDFS],
  followers: ['/network', LibraryChips.FOLLOWERS],
  following: ['/network', LibraryChips.FOLLOWING],
}

@Component({
  selector: 'ddc-library',
  templateUrl: './ddc-library.component.html',
  styleUrls: ['./ddc-library.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    DdcTabsChipsComponent,
    DdcNotAvailableComponent,
    DdcTagButtonComponent,
    InfiniteScrollDirective,
    DdcSmallWordComponent,
    DdcSmallPairComponent,
    DdcLoadingSkeletonComponent,
    DdcCatButtonComponent,
    FormsModule,
    DdcContentCardComponent,
    DdcPdfButtonComponent,
    DdcStatLineChartComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcLibraryComponent implements OnInit, OnDestroy {
  @ViewChild('myTable', { read: ElementRef }) table: ElementRef

  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  private themeSubscription: Subscription

  _userId: number
  customIndex: number
  _theme$: Observable<string>

  // Consolidated tab UI state — replaces ~100 per-type boolean/string/number properties
  _tabUI = createDefaultTabUI()

  // Data arrays
  _words: Word[] = []
  _pairs: Pair[] = []
  _dictionaries: any[] = []
  _notes: Note[] = []
  _quizzes: Quiz[] = []
  _figures: Figure[] = []
  _categories: any[] = []
  _tags: any[] = []
  _pdfs: DaydicPDF[] = []

  // Counts and overview
  _totalCounts: Record<string, number> = {}
  _lastUpdated: Record<string, any> = {}

  // Search
  _searchDataSources: Record<string, MatTableDataSource<DataSearchResult>> = {}
  _searchResults: Record<string, DataSearchResult[]> = {}
  _showSearchResults: Record<string, boolean> = {}
  displayedColumns: string[] = ['type', 'label', 'info', 'time_since_creation']
  _isInputActive = false
  _searchTerm: string = ''
  searchTerm: string = ''

  _searchTypes: Record<string, number> = {
    words: SearchChips.WORD,
    pairs: SearchChips.PAIR,
    dictionaries: SearchChips.DICTIONARY,
    notes: SearchChips.NOTE,
    quizzes: SearchChips.QUIZ,
    figures: SearchChips.FIGURE,
    categories: SearchChips.CATEGORY,
    tags: SearchChips.TAG,
  }

  _defaultChipOption: string = 'overview'
  _iconNames = DaydicTypesIconNames
  _isMobile: boolean = false

  // Dummy data for ECharts line stat components
  _dictionariesLastYearData = {
    name: 'Dictionaries',
    value: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    timeLabels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
  }

  _notesLastYearData = {
    name: 'Notes',
    value: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    timeLabels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
  }

  _figuresLastYearData = {
    name: 'Figures',
    value: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    timeLabels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
  }

  _quizzesLastYearData = {
    name: 'Quizzes',
    value: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    timeLabels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
  }

  scroll_options: AnimationOptions = {
    path: '/assets/lottie/scroll_down.json',
    loop: true,
    autoplay: true,
  }

  loading_options: AnimationOptions = {
    path: '/assets/lottie/loading2.json',
    loop: true,
    autoplay: true,
  }

  private searchServices: Record<string, (term: string) => Observable<any>>

  constructor(
    private cdr: ChangeDetectorRef,
    private logger: DdcLoggerService,
    private libraryState: LibraryStateService,
    public dialog: MatDialog,
    private cat_service: CategoryService,
    private tag_service: TagService,
    private word_service: WordService,
    private pair_service: PairService,
    private dic_service: DictionaryService,
    private note_service: NoteService,
    private quiz_service: QuizService,
    private figure_service: FigureService,
    private overview_service: OverviewService,
    private router: Router,
    private route: ActivatedRoute,
    public themeService: ThemeService,
    private store: Store<AppState>,
    private deleteWordPairDicCommand: DeleteWordPairDicCommand,
    private pdfExportService: PdfExportService,
  ) {
    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    this._theme$ = this.store.select(getTheme)

    // Initialize search infrastructure
    for (const key of TAB_KEYS) {
      this._searchDataSources[key] = new MatTableDataSource<DataSearchResult>()
      this._searchResults[key] = []
      this._showSearchResults[key] = false
      this._totalCounts[key] = 0
    }

    this.searchServices = {
      words: (term: string): Observable<any> => this.word_service.get_searched_words(term),
      pairs: (term: string): Observable<any> => this.pair_service.get_searched_pairs(term),
      dictionaries: (term: string): Observable<any> => this.dic_service.get_searched_dics(term),
      notes: (term: string): Observable<any> => this.note_service.get_searched_notes(term),
      quizzes: (term: string): Observable<any> => this.quiz_service.get_searched_quizzes(term),
      figures: (term: string): Observable<any> => this.figure_service.get_searched_figures(term),
      categories: (term: string): Observable<any> => this.cat_service.get_searched_cats(term),
      tags: (term: string): Observable<any> => this.tag_service.get_searched_tags(term),
    }

    this._tabUI.overview.isSelected = true
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this._isMobile = event.target.innerWidth < 1224
  }
  clearInput(inputElement: HTMLInputElement): void {
    this.searchTerm = ''
    inputElement.value = ''
    this.onSearchChange('word', '')
    inputElement.blur()
  }
  private readonly ITEM_LABEL_FN: Record<string, (item: any) => string> = {
    words: (item: Word): string => item.word,
    pairs: (item: Pair): string => item.word_1.word + ' = ' + item.word_2.word,
    dictionaries: (item: any): string => item.title,
    notes: (item: any): string => item.title,
    quizzes: (item: any): string => item.title,
    figures: (item: any): string => item.title,
  }

  ngOnInit(): void {
    this.checkIfMobile()

    // Subscribe to library state for all content types
    for (const key of LIBRARY_TAB_KEYS) {
      this.libraryState.getItems$(key).subscribe(items => {
        this.setDataArray(key, items)
        if (items.length > 0 && this.ITEM_LABEL_FN[key]) {
          this._lastUpdated[key] = this.ITEM_LABEL_FN[key](items[0])
        }
        this.cdr.detectChanges()
      })

      this.libraryState.getLoading$(key).subscribe(loading => {
        this._tabUI[key].scrollLoading = loading
        this.cdr.detectChanges()
      })
    }

    // Subscribe to total items
    this.libraryState.totalItems$.subscribe(totals => {
      for (const key of LIBRARY_TAB_KEYS) {
        this._totalCounts[key] = totals[key]
      }
      this.cdr.detectChanges()
    })

    // Handle route params
    this.route.params.subscribe(params => {
      const id = params['chip_code']
      this.handleDefaultChipOption(id)
    })
  }

  private setDataArray(key: ContentTypeKey, items: any[]): void {
    switch (key) {
      case 'words':
        this._words = items
        break
      case 'pairs':
        this._pairs = items
        break
      case 'dictionaries':
        this._dictionaries = items
        break
      case 'notes':
        this._notes = items
        break
      case 'quizzes':
        this._quizzes = items
        break
      case 'figures':
        this._figures = items
        break
    }
  }

  checkIfMobile(): void {
    if (window.innerWidth < 500) {
      // 768px is a common breakpoint for mobile devices
      this._isMobile = true
    } else {
      this._isMobile = false
    }
  }

  onSearchChange(type: string, searchValue: string): void {
    this._searchTerm = searchValue
    if (searchValue.length >= 2) {
      const service = this.searchServices[type]
      if (service) {
        this._showSearchResults[type] = true
        service(searchValue).subscribe((res: any) => {
          if (res) {
            this._searchResults[type] = res
            this._searchDataSources[type].data = res
          }
        })
      }
    } else {
      for (const key of Object.keys(this._showSearchResults)) {
        this._showSearchResults[key] = false
      }
    }
  }

  handleDefaultChipOption(id: number): void {
    this.logger.info('DdcLibraryComponent | handleDefaultChipOption() | id:', id)
    const tab = CHIP_ID_TO_TAB[id]
    this.clickedChip(tab || this._defaultChipOption)
  }

  clickedChip(name: string): void {
    this.logger.info('DdcLibraryComponent | clickedChip() | name:', name)

    // Reset all tabs
    for (const key of TAB_KEYS) {
      this._tabUI[key].isSelected = false
    }

    // Activate the selected tab
    const tab = this._tabUI[name as TabKey]
    if (!tab) return
    tab.isSelected = true

    // Load data if not already loaded
    if (name === 'overview') {
      if (!tab.isLoaded) this.getOverview()
    } else if (name === 'categories') {
      if (!tab.isLoaded) {
        tab.currentPage = 1
        this.getCategoriesPage()
      }
    } else if (name === 'tags') {
      if (!tab.isLoaded) {
        tab.currentPage = 1
        this.getTagsPage()
      }
    } else if (name === 'pdfs') {
      this.loadPDFs()
    } else {
      const key = name as ContentTypeKey
      if (!tab.isLoaded) {
        tab.currentPage = 1
        this.loadLibraryTab(key)
      }
    }
  }

  loadPDFs(page: number = 1): void {
    this._tabUI.pdfs.isLoading = true
    this._tabUI.pdfs.isEmpty = false
    this._tabUI.pdfs.fetchError = ''
    this.pdfExportService.get_PDFs_page(page, 10).subscribe({
      next: (res: PdfsResponse) => {
        this._pdfs = res.results || []
        this._tabUI.pdfs.isLoading = false
        this._tabUI.pdfs.isEmpty = this._pdfs.length === 0
        this._totalCounts['pdfs'] = res.count || 0
      },
      error: err => {
        this._tabUI.pdfs.isLoading = false
        this._tabUI.pdfs.isEmpty = true
        this._tabUI.pdfs.fetchError = err?.message || 'Failed to load PDFs.'
      },
    })
  }

  // Generic load method for library state tabs (words, pairs, dictionaries, notes, quizzes, figures)
  loadLibraryTab(key: ContentTypeKey): void {
    this.logger.info(`DdcLibraryComponent | loadLibraryTab(${key})`)
    const tab = this._tabUI[key]
    tab.isLoading = true
    tab.scrollLoading = true
    tab.pageInfoLabel = 'Loading...'
    tab.isEmpty = false
    tab.fetchError = ''
    tab.isPgnLoading = !tab.isPgnLoading

    this.libraryState
      .loadItems(key, true)
      .then(() => {
        tab.end = false
        tab.pageInfoLabel = 'Scroll down to load more'
        tab.isLoading = false
        tab.scrollLoading = false
        tab.isLoaded = true
        tab.isPgnLoading = !tab.isPgnLoading
      })
      .catch(error => {
        this.logger.error(`Error loading ${key}:`, error)
        tab.end = true
        tab.isEmpty = true
        tab.fetchError = error?.message || `Failed to load ${key}`
        tab.pageInfoLabel = 'Error loading items'
        tab.isLoading = false
        tab.scrollLoading = false
        tab.isPgnLoading = !tab.isPgnLoading
      })
  }

  // Generic append/scroll for library state tabs
  appendLibraryTabData(key: ContentTypeKey): void {
    this.logger.info(`DdcLibraryComponent | appendLibraryTabData(${key})`)
    const tab = this._tabUI[key]
    if (!tab.scrollLoading && !tab.end) {
      tab.scrollLoading = true
      tab.pageInfoLabel = 'Loading more...'

      this.libraryState
        .loadMoreItems(key)
        .then(() => {
          tab.end = false
          tab.pageInfoLabel = 'Scroll down to load more'
          tab.scrollLoading = false
        })
        .catch(() => {
          tab.end = true
          tab.pageInfoLabel = 'No more items'
          tab.scrollLoading = false
        })
    }
  }

  onLibraryTabScroll(key: ContentTypeKey): void {
    this.logger.info(`DdcLibraryComponent | onLibraryTabScroll(${key})`)
    this.appendLibraryTabData(key)
  }

  // Backward-compatible scroll handlers for template
  onWordsScroll(): void {
    const tab = this._tabUI.words
    if (!tab.scrollLoading && !tab.end) {
      tab.scrollLoading = true
      tab.pageInfoLabel = 'Loading more...'

      this.libraryState
        .loadMoreWords()
        .then(() => {
          tab.end = false
          tab.pageInfoLabel = 'Scroll down to load more'
          tab.scrollLoading = false
        })
        .catch(() => {
          tab.end = true
          tab.pageInfoLabel = 'No more items'
          tab.scrollLoading = false
        })
    }
  }
  onPairsScroll(): void {
    this.onLibraryTabScroll('pairs')
  }
  onDictionariesScroll(): void {
    this.onLibraryTabScroll('dictionaries')
  }
  onNotesScroll(): void {
    this.onLibraryTabScroll('notes')
  }
  onQuizzesScroll(): void {
    this.onLibraryTabScroll('quizzes')
  }
  onFiguresScroll(): void {
    this.onLibraryTabScroll('figures')
  }

  dashboardElementClicked(element: string): void {
    this.logger.info('DdcDashboardCardComponent: dashboardElementClicked() | element:', element)
    const route = DASHBOARD_ELEMENT_TO_ROUTE[element]
    if (route) {
      this.router.navigate([route[0], route[1]])
    }
  }

  private decrementTotalCount(key: ContentTypeKey): void {
    this._totalCounts[key] = (this._totalCounts[key] || 0) - 1
    this.libraryState.updateTotalCounts(
      this._totalCounts['words'] || 0,
      this._totalCounts['pairs'] || 0,
      this._totalCounts['dictionaries'] || 0,
      this._totalCounts['notes'] || 0,
      this._totalCounts['quizzes'] || 0,
      this._totalCounts['figures'] || 0,
    )
  }

  getOverview(): void {
    this.logger.info('DdcLibraryComponent | getOverview()')
    this._tabUI.overview.isLoading = true

    this.overview_service.get_overview('library').subscribe((res: any) => {
      if (res) {
        // fill in stats
        this._dictionariesLastYearData.value = res.monthly_dics || []
        this._notesLastYearData.value = res.monthly_notes || []
        this._figuresLastYearData.value = res.monthly_figures || []
        this._quizzesLastYearData.value = res.monthly_quizzes || []

        this._totalCounts['categories'] = res.n_cats
        this._totalCounts['tags'] = res.n_tags

        this.libraryState.updateTotalCounts(
          res.n_words,
          res.n_pairs,
          res.n_dics,
          res.n_notes,
          res.n_quizzes,
          res.n_figures,
        )

        this._lastUpdated['words'] = res.last_word
        this._lastUpdated['pairs'] = res.last_pair
        this._lastUpdated['dictionaries'] = res.last_dic
        this._lastUpdated['categories'] = res.last_cat
        this._lastUpdated['tags'] = res.last_tag
        this._lastUpdated['notes'] = res.last_note
        this._lastUpdated['quizzes'] = res.last_quiz
        this._lastUpdated['figures'] = res.last_figure

        this._tabUI.overview.isLoading = false
        this._tabUI.overview.isLoaded = true
        this.cdr.detectChanges()
      }
    })
  }

  openSearchResults(type: number, target: string): void {
    this.logger.info(
      'DdcLibraryComponent | openSearchResults() | type:',
      type,
      ' | target:',
      target,
    )
    this.router.navigate(['/search/'], {
      queryParams: {
        type: type,
        target: target,
      },
    })
  }

  appendCatsData = (): void => {
    this.logger.info('DdcLibraryComponent | appendCatsData()')
    const tab = this._tabUI.categories
    tab.scrollLoading = true
    tab.pageInfoLabel = 'Loading...'
    tab.isPgnLoading = !tab.isPgnLoading

    this.cat_service
      .get_cats_page(tab.currentPage, DefaultPagination.OBJECT_PER_PAGE)
      .subscribe((res: any) => {
        if (res) {
          setTimeout(() => {
            if (res.detail != 'Invalid page.') {
              this._categories = this._categories.concat(res.results)
              tab.end = false
              tab.pageInfoLabel = 'Scroll down to load more'
            } else {
              tab.end = true
              tab.pageInfoLabel = 'All categories loaded.'
            }
            tab.isPgnLoading = !tab.isPgnLoading
            tab.scrollLoading = false
          }, 1000)
        }
      })
  }

  appendTagsData = (): void => {
    this.logger.info('DdcLibraryComponent | appendTagsData()')
    const tab = this._tabUI.tags
    tab.scrollLoading = true
    tab.pageInfoLabel = 'Loading...'
    tab.isPgnLoading = !tab.isPgnLoading

    this.tag_service
      .get_tags_page(tab.currentPage, DefaultPagination.OBJECT_PER_PAGE)
      .subscribe((res: any) => {
        if (res) {
          setTimeout(() => {
            if (res.detail != 'Invalid page.') {
              this._tags = this._tags.concat(res.results)
              tab.end = false
              tab.pageInfoLabel = 'Scroll down to load more'
            } else {
              tab.end = true
              tab.pageInfoLabel = 'All tags loaded.'
            }
            tab.isPgnLoading = !tab.isPgnLoading
            tab.scrollLoading = false
          }, 1000)
        }
      })
  }

  appendPDFsData = (): void => {
    this.logger.info('DdcLibraryComponent | appendPDFsData()')
    const tab = this._tabUI.pdfs
    tab.scrollLoading = true
    tab.pageInfoLabel = 'Loading...'
    tab.isPgnLoading = !tab.isPgnLoading

    this.pdfExportService
      .get_PDFs_page(tab.currentPage, DefaultPagination.OBJECT_PER_PAGE)
      .subscribe((res: any) => {
        if (res) {
          setTimeout(() => {
            if (res.detail != 'Invalid page.') {
              this._pdfs = this._pdfs.concat(res.results)
              tab.end = false
              tab.pageInfoLabel = 'Scroll down to load more'
            } else {
              tab.end = true
              tab.pageInfoLabel = 'All PDFs loaded.'
            }
            tab.isPgnLoading = !tab.isPgnLoading
            tab.scrollLoading = false
          }, 1000)
        }
      })
  }

  onCatsScroll(): void {
    this.logger.info('DdcLibraryComponent | onCatsScroll()')
    this._tabUI.categories.currentPage++
    this.appendCatsData()
  }

  onTagsScroll(): void {
    this.logger.info('DdcLibraryComponent | onTagsScroll()')
    this._tabUI.tags.currentPage++
    this.appendTagsData()
  }

  onPDFsScroll(): void {
    this.logger.info('DdcLibraryComponent | onPDFsScroll()')
    this._tabUI.pdfs.currentPage++
    this.appendPDFsData()
  }

  getCategoriesPage(): void {
    this.logger.info('DdcLibraryComponent | getCategoriesPage()')
    const tab = this._tabUI.categories
    tab.isLoading = true
    tab.scrollLoading = true
    tab.pageInfoLabel = 'Loading...'
    tab.isPgnLoading = !tab.isPgnLoading

    this.cat_service
      .get_cats_page(tab.currentPage, DefaultPagination.OBJECT_PER_PAGE)
      .subscribe((res: any) => {
        if (res) {
          setTimeout(() => {
            this._categories = res.results
            this._totalCounts['categories'] = res.count
            tab.end = false
            tab.pageInfoLabel = 'Scroll down to load more'
            tab.isLoading = false
            tab.scrollLoading = false
            tab.isLoaded = true
            tab.isPgnLoading = !tab.isPgnLoading
          }, 500)
        }
      })
  }

  getTagsPage(): void {
    this.logger.info('DdcLibraryComponent | getTagsPage()')
    const tab = this._tabUI.tags
    tab.isLoading = true
    tab.scrollLoading = true
    tab.pageInfoLabel = 'Loading...'
    tab.isPgnLoading = !tab.isPgnLoading

    this.tag_service
      .get_tags_page(tab.currentPage, DefaultPagination.OBJECT_PER_PAGE)
      .subscribe((res: any) => {
        if (res) {
          setTimeout(() => {
            this._tags = res.results
            this._totalCounts['tags'] = res.count
            tab.end = false
            tab.pageInfoLabel = 'Scroll down to load more'
            tab.isLoading = false
            tab.scrollLoading = false
            tab.isLoaded = true
            tab.isPgnLoading = !tab.isPgnLoading
          }, 500)
        }
      })
  }

  getTags(): void {
    this.logger.info('DdcLibraryComponent | getTags()')
    this.tag_service.get_tags().subscribe((res: any) => {
      if (res) {
        this._tags = res
      }
    })
  }

  // cat and tag
  openEditDialog(type: string, element: any): void {
    this.logger.info('DdcLibraryComponent | openEditDialog() | type:', type, ' | element:', element)

    const dialogRef = this.dialog.open(DdcEditDialogComponent, {
      data: {
        type: type,
        element: element,
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      // Handle any actions after the dialog is closed, e.g., updating the user's profile picture.
      if (result === 'category') {
        this.getCategoriesPage()
      } else if (result === 'tag') {
        this.getTags()
      }
    })
  }

  // Content-type dialog configuration
  private readonly CONTENT_TYPE_DIALOG: Record<
    ContentTypeKey,
    { label: string; dialogKey: string; contentTypeId: number }
  > = {
    words: { label: 'Word', dialogKey: 'word', contentTypeId: ContentTypes.WORD },
    pairs: { label: 'Pair', dialogKey: 'pair', contentTypeId: ContentTypes.PAIR },
    dictionaries: { label: 'Dictionary', dialogKey: 'dictionary', contentTypeId: ContentTypes.DIC },
    notes: { label: 'Note', dialogKey: 'note', contentTypeId: ContentTypes.NOTE },
    quizzes: { label: 'Quiz', dialogKey: 'quiz', contentTypeId: ContentTypes.QUIZ },
    figures: { label: 'Figure', dialogKey: 'figure', contentTypeId: ContentTypes.FIGURE },
  }

  private getDataArray(key: ContentTypeKey): any[] {
    switch (key) {
      case 'words':
        return this._words
      case 'pairs':
        return this._pairs
      case 'dictionaries':
        return this._dictionaries
      case 'notes':
        return this._notes
      case 'quizzes':
        return this._quizzes
      case 'figures':
        return this._figures
    }
  }

  private getMobileDialogConfig(type: string, element: any): MatDialogConfig {
    let themeClass = ''
    this._theme$
      .subscribe(theme => {
        themeClass = theme
      })
      .unsubscribe()
    const config = new MatDialogConfig()
    config.panelClass = ['mobile-sheet-container', themeClass]
    config.position = { bottom: '0' }
    config.width = '100%'
    config.height = '80vh'
    config.maxWidth = '100vw'
    config.hasBackdrop = true
    config.backdropClass = 'mobile-sheet-backdrop'
    config.enterAnimationDuration = '300ms'
    config.exitAnimationDuration = '200ms'
    config.data = { type, element }
    return config
  }

  openReadDialog(key: ContentTypeKey, index: number): void {
    this.logger.info(`DdcLibraryComponent | openReadDialog(${key}) | index:`, index)
    const config = this.CONTENT_TYPE_DIALOG[key]
    const element = this.getDataArray(key)[index]
    const type = `read-${config.dialogKey}`
    if (this._isMobile) {
      this.dialog.open(DdcMobileSheetComponent, this.getMobileDialogConfig(type, element))
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type, element }
      this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  openItemEditDialog(key: ContentTypeKey, index: number): void {
    this.logger.info(`DdcLibraryComponent | openItemEditDialog(${key}) | index:`, index)
    const config = this.CONTENT_TYPE_DIALOG[key]
    const element = this.getDataArray(key)[index]
    const type = `edit-${config.dialogKey}`
    if (this._isMobile) {
      this.dialog.open(DdcMobileSheetComponent, this.getMobileDialogConfig(type, element))
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type, element }
      this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  openDeleteDialog(key: ContentTypeKey, index: number): void {
    this.logger.info(`DdcLibraryComponent | openDeleteDialog(${key}) | index:`, index)
    const config = this.CONTENT_TYPE_DIALOG[key]
    const items = this.getDataArray(key)
    const element = items[index]
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: { type: config.label, element },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: config.contentTypeId,
          object_id: element.id,
        })
        this.deleteWordPairDicCommand.execute().subscribe(success => {
          if (success) {
            const idx = items.findIndex(item => item.id === element.id)
            if (idx !== -1) {
              items.splice(idx, 1)
              this.decrementTotalCount(key)
              if (items.length > 0 && this.ITEM_LABEL_FN[key]) {
                this._lastUpdated[key] = this.ITEM_LABEL_FN[key](items[0])
              }
              this.cdr.detectChanges()
            }
          }
        })
      }
    })
  }

  updateWordBookmark(event: any, index: number): void {
    this._words[index].is_bookmarked = event
  }

  updateNoteBookmark(event: any, index: number): void {
    this._notes[index].is_bookmarked = event
  }

  updateQuizBookmark(event: any, index: number): void {
    this._quizzes[index].is_bookmarked = event
  }

  updateFigureBookmark(event: any, index: number): void {
    this._figures[index].is_bookmarked = event
  }

  removeWordElementFromList(result: any): void {
    const indexToRemove = this._words.findIndex(word => word.word === result.element.word)

    if (indexToRemove !== -1) {
      this.decrementTotalCount('words')
      this._words.splice(indexToRemove, 1)

      if (this._words.length > 0) {
        this._lastUpdated['words'] = this._words[0].word
      }
    }
  }

  // Generic keyboard navigation
  private selectNextRow(tab: TabKey): void {
    const results = this._searchResults[tab] || []
    if (this._tabUI[tab].selectedRowIndex < results.length - 1) {
      this._tabUI[tab].selectedRowIndex++
    }
  }

  private selectPreviousRow(tab: TabKey): void {
    if (this._tabUI[tab].selectedRowIndex > 0) {
      this._tabUI[tab].selectedRowIndex--
    }
  }

  handleKeyDown(tab: TabKey, event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        this.selectNextRow(tab)
        event.preventDefault()
        break
      case 'ArrowUp':
        this.selectPreviousRow(tab)
        event.preventDefault()
        break
      case 'Enter':
        this.enterEvent(tab, this._tabUI[tab].selectedRowIndex)
        event.preventDefault()
        break
    }
  }

  // Backward-compatible keyboard handlers for template
  handleKeyDownWord(event: KeyboardEvent): void {
    this.handleKeyDown('words', event)
  }
  handleKeyDownPair(event: KeyboardEvent): void {
    this.handleKeyDown('pairs', event)
  }
  handleKeyDownDic(event: KeyboardEvent): void {
    this.handleKeyDown('dictionaries', event)
  }
  handleKeyDownCat(event: KeyboardEvent): void {
    this.handleKeyDown('categories', event)
  }
  handleKeyDownTag(event: KeyboardEvent): void {
    this.handleKeyDown('tags', event)
  }

  enterEvent(tab: TabKey, i: number): void {
    this.logger.info(`DdcLibraryComponent | enterEvent(${tab}) | i:`, i)
    const results = this._searchResults[tab] || []
    if (i < 0 || i >= results.length) return

    const item = results[i]

    if (tab === 'words') {
      this.word_service.get_word(item.id).subscribe((res: any) => {
        if (res) {
          const dialogRef = this.dialog.open(DdcEditWordDialogComponent, {
            data: { element: res.res },
          })
          dialogRef.afterClosed().subscribe(() => {})
        }
      })
    } else if (tab === 'pairs') {
      this.pair_service.get_pair(item.id).subscribe((res: any) => {
        if (res) {
          const dialogRef = this.dialog.open(DdcEditPairDialogComponent, {
            data: { element: res.res },
          })
          dialogRef.afterClosed().subscribe(() => {})
        }
      })
    } else if (tab === 'dictionaries') {
      this.router.navigate(['/library/dic/' + item.id + '/' + item.info.owner])
    } else if (tab === 'categories') {
      this.dialog.open(DdcEditDialogComponent, {
        data: { type: 'category', element: item },
      })
    } else if (tab === 'tags') {
      this.dialog.open(DdcEditDialogComponent, {
        data: { type: 'tag', element: item },
      })
    }
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe()
    }
  }
}
